import './style.scss';

import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { v4 as uuid } from 'uuid';

import { useDexWalletsToConnectRecords } from 'hooks/tables';
import { useDebouncedCallback } from 'hooks/utility';
import { useTypedSelector } from 'store';
import { EDexPairFilters } from 'types/filters';
import { IConnectWalletToPairRow } from 'types/tables/dex/connectWalletToPairRow';
import { FiltersPopup } from 'ui/Filters';
import { TableToolbar } from 'ui/Table';
import { TableAddressChip } from 'ui/Table/TableAddressChip/TableAddressChip';
import { TableCheckbox } from 'ui/Table/TableCheckbox/TableCheckbox';
import { TableWrapper } from 'ui/TableWrapper/TableWrapper';
import { EExchange } from 'web3';

import { WalletAddressHead } from './headers';

import { RowComponent, StaticTable, TableHeaders, TBody } from '../../../common';

interface IConnectWalletToPairTableProps {
  setSelectedRecords: (v: IConnectWalletToPairRow[]) => void;
}

const ConnectWalletToPairTable: React.FC<IConnectWalletToPairTableProps> = ({
  setSelectedRecords,
}) => {
  const dexPair = useTypedSelector(store => store.pairs.selectedDexPair)!;
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { hasMore, loading, records, tableRecords, loadMore, rowSelection, selectedRecords } =
    useDexWalletsToConnectRecords();

  useEffect(() => {
    setSelectedRecords(selectedRecords);
  }, [setSelectedRecords, selectedRecords]);

  const columns = useMemo<ColumnDef<IConnectWalletToPairRow>[]>(
    () => [
      {
        id: 'select',
        accessorFn: row => ({ address: row.address, index: row.index }),
        header: ({ table }) => <WalletAddressHead table={table} />,
        cell: ({ row, getValue }) => {
          const data = getValue() as { address: string; index: number | null };

          if (!data) return <>{'-'}</>;

          return (
            <div className="checkbox-area">
              <TableCheckbox
                id={uuid()}
                checked={row.getIsSelected()}
                indeterminate={row.getIsSomeSelected()}
                onChange={row.getToggleSelectedHandler()}
              />
              <div data-row-click-skip="true">
                <TableAddressChip
                  address={data.address}
                  network={dexPair.network}
                  addressType="address"
                  format="full"
                  isImported={data.index === null}
                />
              </div>
            </div>
          );
        },
      },
    ],
    [dexPair.network],
  );

  const table = useReactTable({
    data: tableRecords,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    onRowSelectionChange: rowSelection.set,
    state: {
      rowSelection: rowSelection.get,
    },
  });

  const { rows } = table.getRowModel();
  const virtualizer = useVirtualizer({
    getScrollElement: () => tableContainerRef.current,
    count: useMemo(() => rows.length, [rows]),
    overscan: 5,
    estimateSize: useCallback(() => 44, []),
  });

  const fetchMoreOnBottomReached = useCallback(
    async (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;

        //once the user has scrolled within 200px of the bottom of the table, fetch more data if we can
        if (scrollHeight - scrollTop - clientHeight < 200 && !loading && hasMore) {
          await loadMore();
        }
      }
    },
    [hasMore, loadMore, loading],
  );

  const fetchMoreOnBottomReachedDebounced = useDebouncedCallback(fetchMoreOnBottomReached, 100);

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
    //eslint-disable-next-line
  }, []);

  return (
    <>
      <TableToolbar
        className="mm-connect-dex-wallets-table-toolbar"
        customFilters={
          <FiltersPopup
            dexPair={dexPair}
            exchange={EExchange.dex}
            type={EDexPairFilters.connectWallets}
          />
        }
      />

      <TableWrapper
        hasMore={hasMore}
        loading={loading}
        records={records.get}
        cRef={tableContainerRef}
        heightLimit={400}
        onScroll={e => fetchMoreOnBottomReachedDebounced(e.target as HTMLDivElement)}
        className="mm-connect-dex-wallets-table-container"
      >
        <StaticTable className="mm-common-table">
          <TableHeaders isAllChecked={table.getIsAllRowsSelected()} table={table} />
          <TBody
            paddingTop={virtualizer.getVirtualItems()?.[0]?.start || 0}
            paddingBottom={
              virtualizer.getTotalSize() -
              (virtualizer.getVirtualItems()?.[virtualizer.getVirtualItems().length - 1]?.end || 0)
            }
          >
            {virtualizer.getVirtualItems().map(virtualRow => {
              const row = rows[virtualRow.index];

              return (
                <RowComponent
                  isChecked={row.getIsSelected()}
                  row={row}
                  height={virtualRow.size}
                  key={virtualRow.index}
                  onRowClick={event => {
                    const target = event.target as HTMLElement | null;

                    if (!target) return;

                    if (target.closest('[data-row-click-skip="true"]')) {
                      return;
                    }

                    if (target.closest('a, button, input, textarea, select, label')) {
                      return;
                    }

                    if (typeof row.getCanSelect === 'function' && !row.getCanSelect()) {
                      return;
                    }

                    row.toggleSelected();
                  }}
                />
              );
            })}
          </TBody>
        </StaticTable>
      </TableWrapper>
    </>
  );
};

export { ConnectWalletToPairTable };
